import type {CSSProperties} from 'react';
import {TOUCH_MIN} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeOutreachDraftLook = 'outreach-redraw';
export type WedgeOutreachDraftFieldVariant = 'wedge-outreach-draft-subject';
export type WedgeOutreachDraftAreaVariant = 'wedge-outreach-draft-text-area' | 'wedge-outreach-draft-email-area';

export const isWedgeOutreachDraftLook = (look: string): look is WedgeOutreachDraftLook => look === 'outreach-redraw';
export const isWedgeOutreachDraftFieldVariant = (variant: unknown): variant is WedgeOutreachDraftFieldVariant => variant === 'wedge-outreach-draft-subject';
export const isWedgeOutreachDraftAreaVariant = (variant: unknown): variant is WedgeOutreachDraftAreaVariant =>
  variant === 'wedge-outreach-draft-text-area' || variant === 'wedge-outreach-draft-email-area';

/* The Outreach draft sheet (Wedge, paid plans). Regenerate is a bare ghost button 44px tall with 10px side padding, no border or fill, accent
   ink at weight 650 and 13px type, a 4px gap between its glyph and its label; held while a draft runs it takes the tertiary ink and a default
   cursor. The subject, the text body and the email body share one box: full width, border-box, a 1px card border on a 12px radius, the surface
   fill, 15px type and 10px by 12px padding, with the focus ring removed as shipped. The subject adds weight 600 and a 10px bottom margin; both
   bodies add a vertical resize handle and 1.45 leading, with a 120px minimum height for the text and 220px for the email. */
export function wedgeOutreachDraftStyle(look: WedgeOutreachDraftLook, disabled = false): CSSProperties {
  const r = wedgeExactRole;
  void look;
  return {
    minHeight: TOUCH_MIN, padding: '0 10px', border: 'none', background: 'transparent', color: disabled ? r('labelTer') : r('accent'),
    fontWeight: 650, fontSize: 13, cursor: disabled ? 'default' : 'pointer', fontFamily: r('font'),
    display: 'inline-flex', alignItems: 'center', gap: 4,
  };
}

function box(): CSSProperties {
  const r = wedgeExactRole;
  return {
    width: '100%', boxSizing: 'border-box', border: `1px solid ${r('cardBorder')}`, borderRadius: 12, background: r('surface'), color: r('label'),
    fontSize: 15, fontFamily: r('font'), padding: '10px 12px', outline: 'none',
  };
}

export function wedgeOutreachDraftFieldStyle(variant: WedgeOutreachDraftFieldVariant): CSSProperties {
  void variant;
  return {...box(), fontWeight: 600, marginBottom: 10};
}

export function wedgeOutreachDraftAreaStyle(variant: WedgeOutreachDraftAreaVariant): CSSProperties {
  return {...box(), resize: 'vertical', lineHeight: 1.45, minHeight: variant === 'wedge-outreach-draft-text-area' ? 120 : 220};
}
